import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCode, FaDesktop, FaBriefcase, FaGraduationCap } from 'react-icons/fa';
import ThemeBackground from '../theme/ThemeBackground';
import { ThemeContext } from '../../contexts/ThemeContext';

// Wrap the section with ThemeBackground
const StyledAboutSection = styled(ThemeBackground)`
  padding: 7rem 0;
  position: relative;
  overflow: hidden;

  @keyframes blink {
    0%, 50% {
      border-color: transparent;
    }
    50%, 100% {
      border-color: currentColor;
    }
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  padding-bottom: 1rem;
  font-size: 2.5rem;
  color: var(--secondary-color);
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, var(--secondary-color), var(--primary-color));
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto;
  color: var(--text-color);
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 5fr 7fr;
    align-items: start;
  }
`;

const AboutImageWrapper = styled(motion.div)`
  position: relative;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  aspect-ratio: 1 / 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--card-bg);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: var(--border-radius);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    border: 15px solid var(--primary-color);
    border-radius: 50%;
    bottom: -75px;
    right: -75px;
    opacity: 0.1;
    z-index: -1;
  }
`;

const AboutImage = styled.div`
  width: 70%;
  height: 70%;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  border: 4px solid var(--primary-color);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const Experience = styled.div`
  position: absolute;
  bottom: 30px;
  left: 30px;
  background: var(--primary-color);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  
  span {
    display: block;
    text-align: center;
  }
  
  .years {
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1;
  }
  
  .text {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const AboutContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const AboutTitle = styled.h3`
  margin-bottom: 1.5rem;
  font-size: 2rem;
  position: relative;
  color: var(--secondary-color);
  
  span {
    color: var(--primary-color);
  }
`;

const AboutText = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: var(--text-color);
  font-size: 1.1rem;
`;

const HightlightText = styled.span`
  color: var(--primary-color);
  font-weight: 600;
`;

const FocusAreas = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const FocusArea = styled(motion.div)`
  padding: 1.5rem;
  background-color: var(--card-bg);
  border-radius: var(--border-radius);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease, background-color 0.5s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FocusAreaIcon = styled.div`
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const FocusAreaTitle = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--secondary-color);
`;

const FocusAreaText = styled.p`
  font-size: 0.95rem;
  color: var(--text-color);
`;

// New components for Experience & Education sections
const ExperienceSection = styled(motion.div)`
  margin-top: 4rem;
`;

const ExperienceTitle = styled.h3`
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: var(--secondary-color);
  position: relative;
  display: inline-block;
  padding-bottom: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(to right, var(--secondary-color), var(--primary-color));
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 2px;
    height: 100%;
    background-color: var(--primary-color-light);
    opacity: 0.5;
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  padding-left: 2rem;
  padding-bottom: 2rem;
  
  &:last-child {
    padding-bottom: 0;
  }
  
  &:before {
    content: '';
    position: absolute;
    left: -6px;
    top: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: var(--primary-color);
  }
`;

const TimelinePeriod = styled.div`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  background-color: var(--primary-color-light);
  color: var(--primary-color);
  border-radius: 20px;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
`;

const TimelineTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 0.25rem;
  color: var(--secondary-color);
`;

const TimelineSubtitle = styled.h5`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--primary-color);
  font-weight: 500;
`;

const TimelineText = styled.p`
  font-size: 0.95rem;
  color: var(--text-color);
  line-height: 1.6;
`;

const ProjectLink = styled.span`
  color: var(--primary-color);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
  }
`;

const CaseStudyLink = styled(Link)`
  color: var(--primary-color);
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
    color: var(--accent-color);
  }
`;

const DisabledCaseStudyLink = styled.span`
  color: var(--text-color);
  font-weight: 600;
  opacity: 0.6;
  font-style: italic;
`;

const TimelineNote = styled.p`
  font-size: 0.78rem;
  color: var(--text-color);
  opacity: 0.65;
  margin-top: 0.75rem;
  font-style: italic;
`;

const DecorativeCircle = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(to right, var(--primary-color), var(--accent-color));
  opacity: 0.03;
  z-index: 0;
  
  &.top-left {
    top: -150px;
    left: -150px;
  }
  
  &.bottom-right {
    bottom: -150px;
    right: -150px;
  }
`;

// Animation variants
const fadeInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 }
};

const About = () => {
  const { isLight } = useContext(ThemeContext);

  // Typing animation state
  const [displayedTitle, setDisplayedTitle] = useState('');
  const [titleIndex, setTitleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const titles = ['Full-Stack Engineer', 'Software Engineer'];
  const typingSpeed = 100;
  const deletingSpeed = 50;
  const pauseDuration = 2000;

  // Typing animation effect
  useEffect(() => {
    const currentTitle = titles[titleIndex];

    // Finished typing - pause before deleting
    if (!isDeleting && displayedTitle === currentTitle) {
      const pauseTimer = setTimeout(() => setIsDeleting(true), pauseDuration);
      return () => clearTimeout(pauseTimer);
    }

    // Finished deleting - switch to next title
    if (isDeleting && displayedTitle === '') {
      setIsDeleting(false);
      setTitleIndex((prev) => (prev + 1) % titles.length);
      return;
    }

    // Type or delete one character
    const speed = isDeleting ? deletingSpeed : typingSpeed;
    const timer = setTimeout(() => {
      if (isDeleting) {
        setDisplayedTitle((prev) => currentTitle.slice(0, prev.length - 1));
      } else {
        setDisplayedTitle((prev) => currentTitle.slice(0, prev.length + 1));
      }
    }, speed);

    return () => clearTimeout(timer);
  }, [displayedTitle, isDeleting, titleIndex]);

  return (
    <StyledAboutSection id="about">
      <DecorativeCircle className="top-left" />
      <DecorativeCircle className="bottom-right" />
      
      <div className="container">
        <SectionHeader>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <SectionTitle>About Me</SectionTitle>
            <SectionSubtitle>Get to know my background and what drives me</SectionSubtitle>
          </motion.div>
        </SectionHeader>
        
        <AboutGrid>
          <AboutImageWrapper
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeInLeft}
          >
            <AboutImage>
              <img 
                src="assets/images/A.J. Skidmore Headshot.jpg" 
                alt="AJ Skidmore" 
                onError={(e) => {
                  e.target.src = "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"; // Modern professional avatar
                }}
              />
            </AboutImage>
            <Experience>
              <span className="years">4+</span>
              <span className="text">Years Exp</span>
            </Experience>
          </AboutImageWrapper>
          
          <AboutContent
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeInRight}
          >
            <AboutTitle>
              {displayedTitle}
              <span style={{
                borderRight: '3px solid currentColor',
                marginLeft: '2px',
                animation: 'blink 1s step-end infinite'
              }}>
              </span>
            </AboutTitle>

            <AboutText>
              I'm a <HightlightText>full-stack software engineer</HightlightText> with <HightlightText>4+ years of experience</HightlightText> delivering <HightlightText>70+ production sites and applications</HightlightText> for a diverse range of clients from small business to government. I build custom websites, plugins, and applications, and serve as the primary technical escalation point for complex legacy codebases.
            </AboutText>

            <AboutText>
              What sets me apart is my unique background in <HightlightText>Experience Architecture</HightlightText> from Michigan State University—an interdisciplinary major combining web development, UX & UI design, and user research. This foundation allows me to <HightlightText>bridge the gap between design and development</HightlightText>.
            </AboutText>
            

            
            <FocusAreas>
              <FocusArea
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={fadeInUp}
                transition={{ delay: 0.1 }}
              >
                <FocusAreaIcon>
                  <FaCode />
                </FocusAreaIcon>
                <FocusAreaTitle>Web Development</FocusAreaTitle>
                <FocusAreaText>
                  From WordPress and PHP for client sites to React, TypeScript, and Node.js for modern applications — delivering production sites for clients ranging from small businesses to government municipalities.
                </FocusAreaText>
              </FocusArea>

              <FocusArea
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={fadeInUp}
                transition={{ delay: 0.2 }}
              >
                <FocusAreaIcon>
                  <FaDesktop />
                </FocusAreaIcon>
                <FocusAreaTitle>UX Design</FocusAreaTitle>
                <FocusAreaText>
                  Grounded in formal Experience Architecture training — conducting user research, WCAG 2.1 AA accessibility audits, and creating design systems that translate seamlessly from Figma to production code.
                </FocusAreaText>
              </FocusArea>


            </FocusAreas>
          </AboutContent>
        </AboutGrid>
        
        {/* Experience Section */}
        <ExperienceSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <ExperienceTitle>
            <FocusAreaIcon style={{ display: 'inline-block', marginRight: '10px', fontSize: '1.5rem' }}>
              <FaBriefcase />
            </FocusAreaIcon>
            Work Experience
          </ExperienceTitle>
          
          <TimelineContainer>
            <TimelineItem
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <TimelinePeriod>August 2022 - Present</TimelinePeriod>
              <TimelineTitle>Full-Stack Engineer<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>*</sup></TimelineTitle>
              <TimelineSubtitle>Shumaker Technology Group • Remote • Full-Time</TimelineSubtitle>
              <TimelineText>
                • Collaborate with and provide technical direction to a team of 5 developers across concurrent client projects, conducting code reviews and QA on production deliverables.
              </TimelineText>
              <TimelineText>
                • Manage 350+ WordPress sites across the private and public sectors, including coordinating a fleet-wide response to a critical Elementor Pro security vulnerability (CVSS 9.0).
              </TimelineText>
              <TimelineText>
                • Serve as the primary technical escalation point for complex legacy codebases, including a large-scale e-commerce platform with thousands of SKUs and heavily customized code inherited from a prior development team.
              </TimelineText>
              <TimelineText>
                • Built an enterprise e-commerce platform integrating Salesforce CRM and Stripe payment processing for a software company with $1M+ in funding, and developed the ongoing functionality in the application for renewal invoicing and billing.
              </TimelineText>
              <TimelineText>
                • Conduct recurring ADA and WCAG 2.1 AA audits and remediation for municipal client sites.
              </TimelineText>
              <TimelineText>
                • Delivered 70+ production sites and applications for clients, including municipalities with 25,000+ residents, while managing server administration for client infrastructure.
              </TimelineText>
              <TimelineNote>* Web Developer → Full-Stack Engineer → Lead Full-Stack Engineer</TimelineNote>
            </TimelineItem>
          </TimelineContainer>
        </ExperienceSection>

        {/* Featured Projects Section */}
        <ExperienceSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <ExperienceTitle>
            <FocusAreaIcon style={{ display: 'inline-block', marginRight: '10px', fontSize: '1.5rem' }}>
              <FaBriefcase />
            </FocusAreaIcon>
            Featured Projects
          </ExperienceTitle>

          <TimelineContainer>
            <TimelineItem
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <TimelineTitle>
                <ProjectLink as={Link} to="/projects/eudo">
                  HR Analytics Software — Eudo
                </ProjectLink>
              </TimelineTitle>
              <TimelineText>
                • Engineered an HR analytics dashboard using React, TypeScript, Next.js, and Node.js, implementing a proprietary framework to measure employee satisfaction and predict turnover risk.
              </TimelineText>
              <TimelineText>
                • Architected a scalable data pipeline connecting real-time frontend interfaces with a Firebase backend, using GCP BigQuery for HR metrics analysis and reporting.
              </TimelineText>
              <TimelineText>
                <CaseStudyLink to="/projects/eudo">
                  → View Case Study
                </CaseStudyLink>
              </TimelineText>
            </TimelineItem>

            <TimelineItem
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <TimelineTitle>
                <ProjectLink as={Link} to="/projects/efilmhub">
                  Film Industry Platform — eFilmHub
                </ProjectLink>
              </TimelineTitle>
              <TimelineText>
                • Developed a film industry social platform combining talent discovery and industry networking using React, TypeScript, Next.js, Node.js, and GCP.
              </TimelineText>
              <TimelineText>
                • Implemented scalable cloud architecture with GCP Cloud Storage and real-time database to support exponential user growth.
              </TimelineText>
              <TimelineText>
                <CaseStudyLink to="/projects/efilmhub">
                  → View Case Study
                </CaseStudyLink>
              </TimelineText>
            </TimelineItem>

            {/* Align Charity Matcher hidden
            <TimelineItem
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <TimelineTitle>
                <ExternalProjectLink href="https://align-charity-match.web.app/" target="_blank" rel="noopener noreferrer">
                  Align Charity Matcher
                </ExternalProjectLink>
              </TimelineTitle>
              <TimelineSubtitle>Software Engineer</TimelineSubtitle>
              <TimelineText>
                • Designed and built an AI-powered web application that matches users with charities aligned to their values using React, TypeScript, Next.js, Node.js, and Firebase.
              </TimelineText>
              <TimelineText>
                • Engineered intelligent matching algorithm integrating Claude AI to analyze charity missions and user preferences, delivering personalized nonprofit recommendations.
              </TimelineText>
              <TimelineText>
                • Created end-to-end user experience from initial concept through deployment — conducting user research, prototyping in Figma, and implementing responsive frontend architecture.
              </TimelineText>
              <TimelineText>
                <ExternalCaseStudyLink href="https://align-charity-match.web.app/" target="_blank" rel="noopener noreferrer">
                  → View Live Project
                </ExternalCaseStudyLink>
              </TimelineText>
            </TimelineItem>
            */}

            {/* <TimelineItem
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <TimelineTitle>
                AI Chatbot
              </TimelineTitle>
              <TimelineSubtitle>Software Engineer</TimelineSubtitle>
              <TimelineText>
                • Developed an intelligent chatbot for this portfolio using React, LangChain, and Claude AI API, enabling natural language interactions about my experience, skills, and projects.
              </TimelineText>
              <TimelineText>
                • Engineered custom proxy server with Express.js and LangChain to securely handle API authentication, conversation memory, and context management.
              </TimelineText>
              <TimelineText>
                • Designed conversational UI with real-time streaming responses and contextual awareness, creating an engaging way for visitors to explore my work.
              </TimelineText>
            </TimelineItem> */}
          </TimelineContainer>
        </ExperienceSection>

        {/* Education Section */}
        <ExperienceSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <ExperienceTitle>
            <FocusAreaIcon style={{ display: 'inline-block', marginRight: '10px', fontSize: '1.5rem' }}>
              <FaGraduationCap />
            </FocusAreaIcon>
            Education
          </ExperienceTitle>
          
          <TimelineContainer>
            <TimelineItem
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <TimelinePeriod>December 2023</TimelinePeriod>
              <TimelineTitle>Bachelor's in Experience Architecture</TimelineTitle>
              <TimelineSubtitle>Michigan State University</TimelineSubtitle>
              <TimelineText>
                • Interdisciplinary major combining web development, UX design, and user research
              </TimelineText>
            </TimelineItem>
          </TimelineContainer>
        </ExperienceSection>
      </div>
    </StyledAboutSection>
  );
};

export default About;